import React from "react";
import { ExternalLink, Users, Handshake, Lock } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

/**
 * Team & Partner section:
 *   - Vertragsspieler (contract players) at the top
 *   - Vereins-Partner (club partners) below
 *
 * Contract players list is intentionally rendered from an in-file config so the
 * founder can add players in one place. Placeholder cards fill empty slots.
 */
const CONTRACT_PLAYERS = [
  // Add players here as they get signed. Example format:
  // {
  //   name: "Max Mustermann",
  //   role: "Torhüter 1. Mannschaft",
  //   club: "FC Uitikon",
  //   photo: "https://.../portrait.jpg",
  //   instagram: "https://instagram.com/handle",
  //   quote: "Pro grip. Pro edition.",
  // },
];

const CLUB_PARTNERS = [
  {
    slug: "fc-uitikon",
    name: "FC Uitikon",
    tagline_de: "Mehr als ein Verein · seit 1976",
    tagline_en: "More than a club · since 1976",
    location: "Uitikon (ZH), Schweiz",
    web: "https://fcuitikon.ch/",
    logo: "https://fcuitikon.ch/assets/fcu-logo-BxDMBPiQ.png",
    role_de: "Vereins-Partner · Torhüter-Ausrüstung",
    role_en: "Club partner · Goalkeeper equipment",
    code: "FCU15",
    discount_pct: 15,
  },
];

const TeamPartnersSection = () => {
  const { lang } = useLanguage();
  const isDe = lang === "de";

  const t = {
    kicker_players: isDe ? "Vertragsspieler" : "Contract players",
    title_players: isDe ? "Auf ihren Händen." : "On their hands.",
    subtitle_players: isDe
      ? "Torhüter, die 4keeperz im Wettkampf tragen. Vertragsspieler mit exklusiver Ausrüstungs-Partnerschaft."
      : "Keepers who wear 4keeperz in competition. Contract players with an exclusive equipment partnership.",
    coming_soon: isDe ? "Bald verfügbar" : "Coming soon",
    slot_hint: isDe
      ? "Vertragsspieler folgt in Kürze."
      : "Contract player coming soon.",
    reveal_hint: isDe
      ? "Zwei Spieler bereits unterschrieben — Vorstellung folgt."
      : "Two players already signed — presentation coming soon.",
    kicker_clubs: isDe ? "Vereins-Partner" : "Club partners",
    title_clubs: isDe ? "Auf dem Platz vertrauen sie uns." : "They trust us on the pitch.",
    subtitle_clubs: isDe
      ? "Wir rüsten die Torhüter unserer Vereins-Partner mit Handschuhen aus. Vereinsmitglieder erhalten automatischen Rabatt beim Klick auf den 4keeperz-Sponsoren-Link."
      : "We equip goalkeepers at our club partners. Members get an automatic discount when they click the 4keeperz sponsor link on the club's site.",
    visit_club: isDe ? "Zum Verein" : "Visit club",
    auto_discount: isDe ? "Auto-Rabatt via Sponsor-Link" : "Auto-discount via sponsor link",
  };

  return (
    <section
      id="partners"
      data-testid="team-partners-section"
      className="relative py-20 md:py-28 bg-[#0A0A0A] border-b border-white/5"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 space-y-20 md:space-y-24">
        {/* === CONTRACT PLAYERS === */}
        <div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#00A651]/10 border border-[#00A651]/40 text-[#00A651] font-condensed uppercase tracking-widest text-[10px] px-2.5 py-1 mb-4">
                <Users className="w-3 h-3" />
                {t.kicker_players}
              </div>
              <h2 className="font-brush text-5xl md:text-6xl leading-none text-white">
                {t.title_players}
              </h2>
              <p className="text-white/60 mt-3 max-w-2xl">{t.subtitle_players}</p>
            </div>
          </div>

          {/* Player cards or placeholders */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="contract-players-grid">
            {CONTRACT_PLAYERS.map((p) => (
              <article
                key={p.name}
                data-testid={`contract-player-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="border border-white/10 bg-black hover:border-[#00A651]/40 transition-colors overflow-hidden"
              >
                <div className="aspect-[4/3] bg-black overflow-hidden">
                  {p.photo && (
                    <img
                      src={p.photo}
                      alt={p.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  )}
                </div>
                <div className="p-6 space-y-3">
                  <div>
                    <div className="font-brush text-3xl text-white leading-tight">
                      {p.name}
                    </div>
                    <div className="text-[11px] text-[#00A651] font-condensed uppercase tracking-widest mt-1">
                      {p.role} · {p.club}
                    </div>
                  </div>
                  {p.quote && (
                    <p className="text-white/70 text-sm italic border-l-2 border-[#00A651] pl-3">
                      „{p.quote}“
                    </p>
                  )}
                  {p.instagram && (
                    <a
                      href={p.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-white/50 hover:text-[#00A651] font-condensed uppercase tracking-widest"
                    >
                      Instagram <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </article>
            ))}

            {/* Placeholder slots — always show at least 2 empty until players added */}
            {Array.from({ length: Math.max(0, 2 - CONTRACT_PLAYERS.length) }).map(
              (_, i) => (
                <article
                  key={`slot-${i}`}
                  data-testid={`contract-player-slot-${i}`}
                  className="border border-dashed border-white/10 bg-black/40 flex items-center justify-center min-h-[280px] p-8 text-center relative overflow-hidden group"
                >
                  {/* Silhouette placeholder */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-[0.04] pointer-events-none">
                    <Users className="w-40 h-40 text-white" />
                  </div>
                  <div className="relative space-y-3 max-w-xs">
                    <div className="inline-flex items-center gap-2 border border-[#00A651]/40 text-[#00A651] font-condensed uppercase tracking-widest text-[10px] px-2.5 py-1">
                      <Lock className="w-3 h-3" />
                      {t.coming_soon}
                    </div>
                    <div className="font-brush text-2xl text-white/70">
                      {t.slot_hint}
                    </div>
                    {i === 0 && (
                      <p className="text-white/40 text-xs">{t.reveal_hint}</p>
                    )}
                  </div>
                </article>
              )
            )}
          </div>
        </div>

        {/* === CLUB PARTNERS === */}
        <div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#00A651]/10 border border-[#00A651]/40 text-[#00A651] font-condensed uppercase tracking-widest text-[10px] px-2.5 py-1 mb-4">
                <Handshake className="w-3 h-3" />
                {t.kicker_clubs}
              </div>
              <h2 className="font-brush text-5xl md:text-6xl leading-none text-white">
                {t.title_clubs}
              </h2>
              <p className="text-white/60 mt-3 max-w-2xl">{t.subtitle_clubs}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="club-partners-grid">
            {CLUB_PARTNERS.map((c) => (
              <article
                key={c.slug}
                data-testid={`club-partner-${c.slug}`}
                itemScope
                itemType="https://schema.org/SportsOrganization"
                className="border border-white/10 bg-black hover:border-[#00A651]/40 transition-colors overflow-hidden"
              >
                <meta itemProp="name" content={c.name} />
                <meta itemProp="url" content={c.web} />
                <div className="p-6 md:p-7 flex flex-col gap-5">
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 bg-white p-2 border border-white/10 shrink-0 flex items-center justify-center">
                      <img
                        src={c.logo}
                        alt={c.name}
                        className="max-w-full max-h-full object-contain"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-brush text-3xl text-white leading-tight">
                        {c.name}
                      </div>
                      <div className="text-[11px] text-white/50 font-condensed uppercase tracking-widest mt-1">
                        {isDe ? c.tagline_de : c.tagline_en}
                      </div>
                      <div className="text-[11px] text-white/40 font-condensed uppercase tracking-widest">
                        {c.location}
                      </div>
                    </div>
                  </div>

                  <div className="border-l-2 border-[#00A651] pl-3 py-1">
                    <div className="text-[10px] text-[#00A651] font-condensed uppercase tracking-widest">
                      {isDe ? "Kooperation" : "Partnership"}
                    </div>
                    <div className="text-white/90 text-sm mt-0.5">
                      {isDe ? c.role_de : c.role_en}
                    </div>
                  </div>

                  {c.discount_pct && (
                    <div className="flex items-center gap-3 border border-[#00A651]/30 bg-[#00A651]/5 px-3 py-2">
                      <div className="text-xs text-white flex-1">
                        <span className="font-display text-[#00A651] text-base leading-none">
                          {c.discount_pct}%
                        </span>{" "}
                        {isDe ? "Rabatt für Vereinsmitglieder" : "discount for club members"}
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    <a
                      href={c.web}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`club-partner-${c.slug}-visit`}
                      className="inline-flex items-center gap-1.5 border border-[#00A651] text-[#00A651] hover:bg-[#00A651] hover:text-black font-condensed uppercase tracking-widest text-[11px] px-3 py-2 transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {t.visit_club}
                    </a>
                  </div>
                </div>
              </article>
            ))}

            {/* Partnership invite for more clubs */}
            <article
              data-testid="club-partner-invite"
              className="border border-dashed border-white/15 bg-black/40 p-6 md:p-7 flex flex-col justify-center items-center text-center gap-3 min-h-[280px]"
            >
              <div className="w-12 h-12 border border-white/15 flex items-center justify-center">
                <Handshake className="w-5 h-5 text-white/40" />
              </div>
              <div className="font-brush text-2xl text-white/80">
                {isDe ? "Dein Verein hier?" : "Your club here?"}
              </div>
              <p className="text-white/50 text-sm max-w-xs">
                {isDe
                  ? "Wir statten die Torhüter deines Vereins zu Sonderkonditionen aus. Ideal für Junioren-Abteilungen und Fanionteams."
                  : "We equip your club's keepers at special rates. Ideal for youth divisions and first teams."}
              </p>
              <a
                href="mailto:info@4keeperz.ch?subject=Vereins-Partnerschaft"
                className="mt-1 inline-flex items-center gap-1.5 border border-[#00A651] text-[#00A651] hover:bg-[#00A651] hover:text-black font-condensed uppercase tracking-widest text-[11px] px-3 py-2 transition-colors"
              >
                {isDe ? "Kontakt aufnehmen" : "Get in touch"}
                <ExternalLink className="w-3 h-3" />
              </a>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamPartnersSection;
